import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { Price } from "@/components/ui/price";
import { Spinner } from "@/components/ui/spinner";

interface PlaceholderPageProps {
  title: string;
  description: string;
}

/**
 * Not a feature page — a literal "nothing is built here yet" placeholder,
 * used only at "/" during bootstrap so the router has something real to
 * render and the app is genuinely runnable end-to-end. Swapped out for the
 * real Home screen (docs/ui/ui-architecture.md) when that page is
 * implemented; this component is deleted then, not extended.
 *
 * Renders a handful of the shared components (`ThemeToggle`, `Badge`,
 * `Chip`, `Price`, `Spinner`) so the design-system foundation is visibly
 * exercised end-to-end without needing an actual page built on top of it.
 */
export function PlaceholderPage({ title, description }: PlaceholderPageProps) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <ThemeToggle />
      <div className="space-y-2">
        <h1 className="text-h1 text-foreground">{title}</h1>
        <p className="max-w-sm text-body text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Badge variant="success">Verified</Badge>
        <Badge variant="warning">Pending</Badge>
        <Badge variant="danger">Suspended</Badge>
        <Chip label="Electronics" isSelected />
        <Chip label="Under ₹5,000" isRemovable onRemove={() => {}} />
        <Price value={12999} originalValue={15999} />
        <Spinner size="standalone" />
      </div>
    </div>
  );
}
